// ============================================
// patrolCore: M1 巡回コアサービス (Stage 2+3)
// ============================================
import { supabase } from '../lib/supabase'
import { CHANGE_ORG_ID } from '../lib/auth/orgConstants'
import { logger } from '../lib/logger'
import { ERR } from '../lib/errorCodes'
import { buildPrevFromRows, detectSameDayMultiSave, sameDayRowsOf, withSavers } from './prevBaseline'
import { sameExchanges } from './meterBoundary'
import { getStoreMeta } from '../lib/localStore/patrolRecords'
import { isOfflineContinuation } from '../lib/auth/offlineGate' // D-304: 圏外継続中は tier-3(Supabase 直読み)を止める
import { Sentry } from '../lib/sentry' // SPEC-PATROL-BASELINE-FETCH-RESILIENCE-V1 (D-473) IMPL_6: 異常検知 RPC の失敗を握り潰さない

// JST の今日の日付文字列 (YYYY-MM-DD)
function todayJST() {
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Tokyo' })
}

// SPEC-PATROL-PLAYPRICE-PAYLOAD-FIX-01 (D-131): 単価解決。ブース単価を第一優先、null/未取得なら機種マスタ
// meter_unit_price、それも無ければ 100。usePatrolForm.js L77 / collectionPrep.js L176 と同一思想 (既存踏襲)。
export function resolvePlayPriceValue(boothPrice, modelUnitPrice) {
  return Number(boothPrice) || Number(modelUnitPrice) || 100
}

// 巡回保存の単価解決。取得元は STORE_STORE_META (IndexedDB の店舗単位キャッシュ、getPatrolMachines 由来で
// booths[].play_price と machine_models.meter_unit_price を保持)。Supabase 往復は発生させない (AC3)。
// この関数は storeSync のバックグラウンド同期からのみ呼ばれ、UIの0秒体感パスには乗らない。
export async function resolvePlayPriceForBooth({ storeCode, machineCode, boothCode }) {
  let boothPrice = null, modelUnitPrice = null
  const meta = storeCode ? await getStoreMeta(storeCode) : null
  for (const m of (meta?.machines ?? [])) {
    const model = Array.isArray(m.machine_models) ? m.machine_models[0] : m.machine_models
    const b = (m.booths ?? []).find(x => x.booth_code === boothCode)
    if (b) { boothPrice = b.play_price ?? null; modelUnitPrice = model?.meter_unit_price ?? null; break }
    if (machineCode && m.machine_code === machineCode) modelUnitPrice = model?.meter_unit_price ?? modelUnitPrice
  }
  return resolvePlayPriceValue(boothPrice, modelUnitPrice)
}

/** UI / 比較用に文字列正規化 */
function strNorm(v) {
  if (v == null || v === '') return ''
  return String(v).trim()
}

/**
 * SPEC-ENTRY-TYPE-SET-EXT-V1 (D-755): 追加設定(set_ext / jsonb)から「実際に設定されている項目」だけを残す。
 * ★空欄の key は落とす。空文字で jsonb を膨らませない。1 つも残らなければ空オブジェクト。
 * ★この「何を設定済みと見なすか」の判断は **ここ 1 か所**(D-616)。
 *   保存 patch 側(PatrolBoothInputPage の buildOptionalPatch)も同じこの関数を使う。
 *   片方だけ直すと「保存はされるのに入替と判定されない」ようなずれが生まれる。
 * @param {Record<string, unknown>|null|undefined} o
 * @returns {Record<string, string>}
 */
export function normalizeSetExt(o) {
  if (!o || typeof o !== 'object') return {}
  const out = {}
  for (const [k, v] of Object.entries(o)) {
    const t = String(v ?? '').trim()
    if (t !== '') out[k] = t
  }
  return out
}

/**
 * D-755: set_ext の比較用キー。★キーの並び順に依存させない。
 *   applyPrevFields の `{...p.set_ext}` と人の入力順で順番が違うだけで偽の replace になるため、
 *   JSON.stringify では比べない。
 */
function setExtKey(o) {
  const n = normalizeSetExt(o)
  return Object.keys(n).sort().map(k => `${k}=${n[k]}`).join('&')
}

// SPEC-LF1-REPLAY-CONSTRAINT-NORMALIZE-01: DB CHECK chk_stock2/3_present_when_out2/3 =
// (out_meter_N IS NULL OR stock_N IS NOT NULL)。out_meter_N があるのに stock_N が無い
// payload は POST 400。空 stock=0 のアプリ意味に忠実に補完する (捏造ではない)。
// INSERT / UPDATE payload を送信前に正規化する。
function normalizeSlotStock(payload) {
  if (payload.out_meter_2 != null && payload.stock_2 == null) payload.stock_2 = 0
  if (payload.out_meter_3 != null && payload.stock_3 == null) payload.stock_3 = 0
  return payload
}

/**
 * getLastReadingForBooth の完全列（SELECT 欠落 → UI 空白・NULL 上書り事故防止）
 */
export const LAST_READING_SELECT =
  // SPEC-PRIZE-ID-MULTI-ROW-V1 (D-495) ITEM_3: 景品行2/3 の紐付け列。tier-3(Supabase 直)でも前回値が
  //   prize_id_2/3 を運べるようにする(欠けると保存後に開き直したとき未選択に戻り D-490 のバッジが誤発火)。
  'reading_id, booth_code, prize_id, prize_id_2, prize_id_3, prize_name, prize_name_2, prize_name_3, ' +
  'set_a, set_c, set_l, set_r, set_o, ' +
  'prize_stock_count, prize_restock_count, stock_2, stock_3, restock_2, restock_3, ' +
  'theoretical_stock, payout_rate, ' +
  'prize_cost, prize_cost_1, prize_cost_2, prize_cost_3, ' +
  // SPEC-PATROL-PRIZE-PREFILL-REPLACE-VISIBLE-FIX-01 (D-094): entry_type/created_at を追加し、
  // getLastReadingForBooth(tier-3) も buildPrevFromRows で 景品=最新any / メーター=patrol を合成できるようにする。
  'entry_type, created_at, ' +
  // SPEC-PATROL-DUAL-IN-METER-01 (D-156) ITEM_4: 2本目以降の IN も前回値プレフィルするため列追加。
  'in_meter, in_meter_2, in_meter_3, in_meter_4, out_meter, out_meter_2, out_meter_3, patrol_date, read_time, ' +
  // SPEC-METER-EPOCH-RESET-01 (D-165) P1: メーター区間フラグと旧最終値(migration適用済=本番実在)。前回値経路が区間起点を判別できるように。
  'is_meter_reset, prev_meter_final, ' +
  // SPEC-METER-EXCHANGE-REPORT-01 (D-994): 交換/一周の報告(構造化)と、その日のイベント行を決める実効時刻。
  //   ★DB 側の列(20261005100100)が先に当たっていること(無いと前回値の取得が 400 で落ちる。適用順は D-994.md)。
  'meter_exchanges, updated_at, out_meter_4'

/** INSERT 時に prev から補完するオプション列（触ってない値＝規定値） */
const OPTIONAL_DEFAULT_KEYS = [
  'prize_id',
  // D-495 ITEM_3: 行2/3 の紐付けも「触ってない値=規定値」として prev から引き継ぐ。prize_name_2/3 だけ
  //   引き継いで prize_id_2/3 を落とすと、名前だけ残って紐付けが消える行を新規に作ってしまう(D-100 の轍)。
  'prize_id_2',
  'prize_id_3',
  'prize_name',
  'prize_name_2',
  'prize_name_3',
  'set_a',
  'set_c',
  'set_l',
  'set_r',
  // SPEC-METER-EXCHANGE-BOUNDARY-UNIFY-01 (D-984) ITEM_A: set_o(メモ)は外す。
  //   ★prevBaseline.js の PREV_PRIZE_KEYS と対になる経路。ここに残すと、たとえ PREV_PRIZE_KEYS 側で
  //     表示プリフィルを止めても、INSERT 時(replace/collection 新規行、オフライン同期含む)に
  //     defaultsFromPrev 経由で前回の set_o が新しい行へそのまま書き込まれてしまう(D-978 の本体)。
  //     表示(prevBaseline)と書き込み(ここ)の両方を外して初めて「交換はその行でしか宣言されない」になる。
  'prize_cost',
  'prize_cost_1',
  'prize_cost_2',
  'prize_cost_3',
]

// SPEC-LF1-REKEY-MERGE-FIX-01 (D-808) AC2: OPTIONAL_DEFAULT_KEYS の抽出を 1 箇所にする(D-616)。
//   行が「持っている」キーだけを拾う(undefined は拾わない = 触っていないを保つ)。null は拾う(明示的な空)。
export function pickOptionalKeys(row) {
  if (!row) return {}
  const o = {}
  for (const k of OPTIONAL_DEFAULT_KEYS) {
    if (Object.prototype.hasOwnProperty.call(row, k) && row[k] !== undefined) {
      o[k] = row[k]
    }
  }
  return o
}

function pickOptionalDefaultsFromPrev(prev) {
  return pickOptionalKeys(prev)
}

/**
 * optionalPatch: ユーザーが触ったフィールドのみ。キーが無い列は UPDATE しない（NULL 上書き防止）
 * 値は null 許容（明示クリア）。空文字は null に正規化。
 */
function normalizeOptionalPatch(patch) {
  if (!patch || typeof patch !== 'object') return {}
  const out = {}
  for (const [k, v] of Object.entries(patch)) {
    if (v === '' || v === undefined) {
      out[k] = null
    } else {
      out[k] = v
    }
  }
  return out
}

/**
 * 今日の巡回記録をブースコードでバルク取得
 * @returns Map<boothCode, { readingId, readTime }>
 */
export async function getTodayReadingsMap(boothCodes) {
  if (!boothCodes.length) return {}
  if (isOfflineContinuation()) return {} // D-304 IMPL_4: 守衛でも止まるが、無駄なリクエストと警告を出さないため明示 skip
  const today = todayJST()
  // eslint-disable-next-line supabase-guard/require-order -- LEGACY(D-330): 是正保留
  const { data } = await supabase
    .from('meter_readings')
    .select('booth_code, reading_id, read_time')
    .in('booth_code', boothCodes)
    .eq('patrol_date', today)
  const map = {}
  for (const r of (data ?? [])) {
    map[r.booth_code] = { readingId: r.reading_id, readTime: r.read_time }
  }
  return map
}

/**
 * ブース直近読み値取得（前回値表示 + 入替判定用）
 */
export async function getLastReadingForBooth(boothCode) {
  // D-304 IMPL_4/判断5: 圏外継続中は tier-3 を呼ばず {prev:null, error:null} で返す。
  //   error を null にするのが要点=呼び出し側の error 分岐に入れず、cold booth と同じ経路で tier-2 の結果を活かす。
  //   前回値が無いこと自体は正常系。赤いエラーを出すと現場が入力をやめる(バッジで継続中と分かれば十分)。
  if (isOfflineContinuation()) return { prev: null, error: null, skipped: 'offline-continuation' }
  // SPEC-PATROL-PRIZE-PREFILL-REPLACE-VISIBLE-FIX-01 (D-094): tier-3 も合成方式に統一。
  // 単一行 (最新any) だと replace 直後は in_meter=0 の replace 行が景品もメーターも供給してしまう。
  // 直近複数行を取り buildPrevFromRows で 景品=最新any / メーター=patrol に分離する。
  // SPEC-PATROL-PREV-METER-FAIL-SAFE-01 (D-160) ITEM_1/ITEM_2: error を握り潰さず {prev, error} で返す。
  //   従来は error を bind すらせず失敗で data undefined → null 化し、cold booth と区別できず無ログで前回値が空欄化していた。
  const { data, error } = await supabase
    .from('meter_readings')
    .select(LAST_READING_SELECT)
    .eq('booth_code', boothCode)
    .order('patrol_date', { ascending: false })
    .order('read_time', { ascending: false })
    .limit(11)
  if (error) {
    logger.error?.('ERR-LF1-LAST-READING-FAIL', { tier: 'tier3-supabase', boothCode, message: error.message })
    return { prev: null, error }
  }
  // SPEC-PATROL-MULTISAVE-WARN-TIER3-V1 (D-578) ITEM_1: 取ってきた生行も返す。
  //   ★従来は捨てていたので、tier-3 経路では D-577 の複数人保存警告が出せなかった。
  //   ★既存の呼び出し側は { prev, error } だけを見ているので、キーを 1 つ足すだけで壊れない。
  return { prev: buildPrevFromRows(data ?? []), error: null, rows: data ?? [] }
}

/**
 * D-994 gate_3 差し戻し 2 回目 E: 指定日より前の行だけで前回値を作る(巡回画面の「前の日以前の前回値」)。
 *   ★IDB の窓(15 行)が全部今日の行だった(同じ日に何回も保存した)ときの取り直し用。圏外継続中は引かない。
 * @returns {Promise<object|null>}
 */
export async function getLastReadingBeforeDate(boothCode, beforeDate) {
  if (!boothCode || !beforeDate || isOfflineContinuation()) return null
  const { data, error } = await supabase
    .from('meter_readings')
    .select(LAST_READING_SELECT)
    .eq('booth_code', boothCode)
    .lt('patrol_date', beforeDate)
    .order('patrol_date', { ascending: false })
    .order('read_time', { ascending: false })
    .limit(11)
  if (error) return null
  return buildPrevFromRows(data ?? [])
}

/**
 * SPEC-PATROL-MULTISAVE-WARN-TIER3-V1 (D-578) ITEM_1/ITEM_2:
 * 同じ巡回日に複数人が保存しているかを返す。tier を問わず同じ材料の作り方で判定する。
 *
 * ★★実装時に分かったこと: baseline の RPC(fn_store_booth_recent_readings_json)も
 *   tier-3 の LAST_READING_SELECT も、保存者の列を 1 つも運んでいない。
 *   つまり D-577 の警告は tier-2 でも実際には出ていなかった。
 * ★保存者は**別建ての best-effort な問い合わせ**で足す。理由:
 *   (1) LAST_READING_SELECT に列を足すと、その列が本番に無かったときに
 *       前回値の取得そのものが 400 で落ちる。前回値は現場が止まる経路なので賭けない。
 *   (2) この問い合わせが失敗しても、警告が出ないだけで前回値には一切影響しない。
 * ★同じ巡回日の行が 2 件以上あるときだけ問い合わせる(毎回は叩かない)。
 *
 * @param {string} boothCode
 * @param {Array<object>} rows tier-2(IDB baseline)or tier-3(Supabase)の生行
 * @returns {Promise<{patrol_date: string, rows: number, savers: string[]}|null>}
 */
export async function fetchSameDayMultiSave(boothCode, rows) {
  // 行に保存者が乗っていればそれで判定する(将来 RPC/SELECT が運ぶようになったらここで完結する)。
  const direct = detectSameDayMultiSave(rows)
  if (direct) return direct

  const sameDay = sameDayRowsOf(rows)
  if (sameDay.length === 0) return null
  const patrolDate = String(sameDay[0]?.patrol_date ?? '')
  if (!patrolDate) return null

  try {
    const { data, error } = await supabase
      .from('meter_readings')
      .select('reading_id, created_by')
      .eq('booth_code', boothCode)
      .eq('patrol_date', patrolDate)
      .order('created_at', { ascending: false })
      .limit(20)
    // ★列が無い/権限が無い等はここで静かに終わる。前回値の経路には触らない。
    if (error || !Array.isArray(data)) return null
    const byId = {}
    for (const r of data) if (r?.reading_id) byId[String(r.reading_id)] = r.created_by
    return detectSameDayMultiSave(withSavers(rows, byId))
  } catch (_e) {
    return null
  }
}

// ─────────────────────────────────────────────────────────
// M1 Stage 3: entry_type 自動判定
// ─────────────────────────────────────────────────────────

/**
 * 同一ブース連続2レコードの差分から entry_type を判定
 *
 * @param {object|null} prev  - 直前レコード (DB行)
 * @param {object}       next  - 今回入力値 { inMeter, outMeter, prizeName?, setA? ... setO? }
 * @param {boolean}      isCollection - 集金として寄録（stores.is_collection_day かつチェック ON）
 * @returns 'patrol' | 'replace' | 'collection'
 */
export function classifyEntryType({ prev, next, isCollection = false }) {
  if (isCollection) return 'collection'
  if (!prev) return 'patrol'

  const numIn  = next.inMeter  !== '' && next.inMeter  != null ? parseFloat(next.inMeter)  : null
  const numOut = next.outMeter !== '' && next.outMeter != null ? parseFloat(next.outMeter) : null

  const meterChanged =
    Number(prev.in_meter)  !== numIn ||
    Number(prev.out_meter) !== numOut

  if (meterChanged) return 'patrol'

  // SPEC-ENTRY-TYPE-PRIZE-ID-COMPARE-V1 (D-753): 同一性は **ID** で見る。表示名では見ない。
  //   ★真因: next.prizeName は画面 state そのもので、D-596(SPEC-PATROL-PRIZE-INPUT-SHORT-DISPLAY-ONLY-V1)
  //     の短縮名差し替えが **非同期でこれを書き換える**。prev.prize_name は保存値(フルネーム)のままなので、
  //     短縮名が入った瞬間に食い違い、何も触っていないのに replace に化けていた(実機報告 2026-09-15)。
  //   ★D-596 は「保存値(prize_name 列)は変えない」を安全の根拠にしていたが、同じ state を読む
  //     別の消費者(ここ)が居た。★影響範囲は「保存されるか」ではなく「読む者が誰か」で数える。
  //   ★表示名は短縮・改名・表記ゆれで動くことが前提の値で、同一性の根拠にならない。
  //   ★ただし ID が片方でも無い場面(手入力で紐付きが無い行)は実在するので、そのときだけ
  //     従来どおり名前で比べる。★名前から prize_id を推測することはしない(D-492 / D-450)。
  // SPEC-ENTRY-TYPE-HUMAN-TOUCH-GATE-V1 (D-754): 人が景品欄を触っていないなら、欄の値がどう動こうと
  //   それは「人が替えた」ではない。★アプリが自分で書いた値を、人の意思として記録に残さない。
  //   ★D-753 の ID 比較では届かない場面がある: D-450 の自動補完(直近補充景品)は
  //     **prev が景品を 1 つも持たないブース**で走るので、prev.prize_id が無く名前比較に落ちる。
  //     `'' ≠ 自動補完された名前` で replace に化け、willCreateLoadMovement が無条件 true になり、
  //     補充していないのに load/unload movement(景品在庫)が記録されうる状態だった。
  //   ★「アプリが入れたか人が入れたか」の印は新設しない。touched.prizeName が既にその印である
  //     (BoothInputForm の onChange / onSelect = 人の操作の中でしか立たない)。二重に持たない(D-616)。
  //   ★逆向き(自動補完側で touched を立てる)は禁じ手。touched は「保存 patch に列を載せるか」も
  //     決めているので、人が触っていない景品名が DB へ書かれる(D-508 の実害と同型)。
  //   ★prizeTouched が渡されない(undefined)ときは従来どおり。既存の呼び出しと検査を 1 行も変えない。
  const prizeUntouched = next.prizeTouched === false
  const prevPrizeId = prev.prize_id ?? null
  const nextPrizeId = next.prizeId ?? null
  const prizeChanged = prizeUntouched
    ? false
    : prevPrizeId != null && nextPrizeId != null
      ? nextPrizeId !== prevPrizeId
      : strNorm(next.prizeName) !== strNorm(prev.prize_name)

  const settingChanged =
    // SPEC-ENTRY-TYPE-SET-EXT-V1 (D-755): 追加設定(D-592)も set_a〜set_o と同じ扱いにする。
    //   ★D-592 は欄を増やしたが、判定側まで届いていなかった(patrolCore に set_ext の文字列が 1 つも無かった)。
    //   ★「新しい欄だけルールが違う」は人が覚えられない。欄を増やしたら規則もそこまで届かせる。
    setExtKey(next.setExt) !== setExtKey(prev.set_ext) ||
    strNorm(next.setA) !== strNorm(prev.set_a) ||
    strNorm(next.setC) !== strNorm(prev.set_c) ||
    strNorm(next.setL) !== strNorm(prev.set_l) ||
    strNorm(next.setR) !== strNorm(prev.set_r) ||
    strNorm(next.setO) !== strNorm(prev.set_o)

  if (prizeChanged || settingChanged) return 'replace'

  return 'patrol'
}

// ─────────────────────────────────────────────────────────
// M1 Stage 2+3: 巡回記録保存
// ─────────────────────────────────────────────────────────

const TODAY_EXISTING_SELECT =
  'reading_id, in_meter, out_meter, prize_stock_count, prize_restock_count, ' +
  'prize_name, prize_id, set_a, set_c, set_l, set_r, set_o, prize_cost, updated_at, ' +
  'stock_2, stock_3, meter_exchanges'

/**
 * 巡回記録 UPSERT / INSERT
 *
 * optionalPatch: ユーザー操作のあった列のみ（差分）。UPDATE 時はこれらのキーのみ列更新。
 * defaultsFromPrev: INSERT 時・replace/collection INSERT 時に optional を prev から補完。
 *
 * entry_type 別動作:
 *   'patrol'     → (booth_code, patrol_date) で1レコード収束、コア値＋optional 差分とも変化なしなら skip
 *   'replace'    → 新規 INSERT（optional は prev マージ＋patch）
 *   'collection' → 新規 INSERT
 */
/**
 * SPEC-RESTOCK-OVERWRITE-FIX-AND-TRACE-V1 (D-608): 補充欄の 3 状態を 1 箇所で決める。
 *   値がある → その値 / 明示 0 → 0 / 送られていない(空・null)→ null(未確定)
 *   ★0 で埋めない。0 を書くと「触っていない」が「補充ゼロ」として保存され、
 *     同じ日の先発行の補充を新しい行が黙って潰す(本番の型①)。
 *   ★加算にはしない(却下3)。再送・オフライン同期で二重計上する。
 *   ★判定を 2 ヶ所に分けない。INSERT も UPDATE もここを通す。
 */
export function isRestockProvided(prizeRestock) {
  return prizeRestock !== '' && prizeRestock != null
}
export function restockValue(prizeRestock) {
  if (!isRestockProvided(prizeRestock)) return null
  const n = parseInt(prizeRestock, 10)
  return Number.isFinite(n) ? n : null
}

export async function savePatrolReading({
  boothCode,
  storeCode,
  machineCode,
  inMeter,
  outMeter,
  prizeStock,
  prizeRestock,
  entryType = 'patrol',
  staffId,
  optionalPatch = {},
  defaultsFromPrev = null,
  // SPEC-LF1-IDEMPOTENT-SYNC-01 D1/D2/D3: LF1 replay carries the queued record's own values.
  patrolDate,        // D1: queued record の patrol_date (未指定=UI直接保存 → todayJST)
  readingId,         // D2: 冪等キー (r.localId)。INSERT 時に reading_id として使う
  clientTimestamp,   // D3: r.createdLocally。UPDATE stale-guard 判定に使う
}) {
  if (!CHANGE_ORG_ID) {
    logger.error(ERR.AUTH_001, { message: 'CHANGE_ORG_ID is not set', boothCode })
    return { ok: false, errCode: ERR.AUTH_001, message: 'organization_id が設定されていません', raw: null }
  }

  // D1: patrol_date と既存行 lookup は patrolDate を優先 (LF1 が D 日作成→D+k replay しても D 日行になる)
  const today  = patrolDate ?? todayJST()
  const now    = new Date().toISOString()
  const numIn  = inMeter      !== '' && inMeter      != null ? parseFloat(inMeter)       : null
  const numOut = outMeter     !== '' && outMeter     != null ? parseFloat(outMeter)      : null
  const numStk = prizeStock   !== '' && prizeStock   != null ? parseInt(prizeStock, 10)  : 0
  // SPEC-PATROL-RESTOCK-CARRY-FIX-01 (D-350) DEFECT_B: 補充が「未入力」か「明示 0」かを区別する。
  //   未入力(null/空)は UPDATE 経路で prize_restock_count を送らない(既存の補充を NULL/0 で上書きしない)。
  //   明示 0 は従来どおり 0 を送る(打ち間違いの訂正を残す)。空とユーザ入力0の区別は D-226 A4 と同原則。
  // SPEC-RESTOCK-OVERWRITE-FIX-AND-TRACE-V1 (D-608): 未入力は null(未確定)。0 で埋めない。
  //   ★D-350 は UPDATE 経路だけを直した。残っていたのは INSERT 経路で、
  //     未入力を 0 にしていたため replace/collection の新規行が「補充 0」を持って最新になり、
  //     同じ日の先発 patrol 行の補充を画面上で潰していた(本番の型①の実例が全部この形)。
  //   ★明示 0 は従来どおり 0 を送る(打ち間違いの訂正を残す)。D-226 A4 と同原則。
  const restockProvided = isRestockProvided(prizeRestock)
  const numRst = restockValue(prizeRestock)

  // ★D-608 ITEM_2(ひろ指示のログ): 補充の値がどう書かれたかを毎回残す。
  //   ★現場の操作は増やさない。画面には何も出さない。既存の logger をそのまま使う。
  //   ★restock_before は既存行が見つかってから分かるので、下の保存経路で改めて出す。
  logger.info('patrol_save_attempted', {
    boothCode,
    patrol_date: today,
    organization_id: CHANGE_ORG_ID,
    entryType,
    has_in_meter: numIn != null,
    has_out_meter: numOut != null,
    patch_keys: Object.keys(optionalPatch ?? {}),
  })

  try {
    const patch = normalizeOptionalPatch(optionalPatch)
    const defaults = pickOptionalDefaultsFromPrev(defaultsFromPrev)
    const mergedOptionalForInsert = { ...defaults, ...patch }

    // SPEC-METER-READINGS-ORG-AND-DENORM-FIX-01 (D-065) F2: 引数未供給時は booth_code から導出し
    // denorm null 保存を構造的に不能化 (machine_code=末尾 -B\d+ 除去 / store_code=先頭ハイフン前)。
    const resolvedStoreCode   = storeCode   ?? boothCode.split('-')[0]
    const resolvedMachineCode = machineCode ?? boothCode.replace(/-B\d+$/, '')
    // D-131: 単価を payload に含める。ブース単価→機種マスタ→100 の順で店舗キャッシュ(IDB)から解決。Supabase往復なし。
    const playPrice = await resolvePlayPriceForBooth({ storeCode: resolvedStoreCode, machineCode: resolvedMachineCode, boothCode })

    const basePayload = {
      booth_id:            boothCode,
      full_booth_code:     boothCode,
      booth_code:          boothCode,
      store_code:          resolvedStoreCode,
      machine_code:        resolvedMachineCode,
      patrol_date:         today,
      read_time:           now,
      in_meter:            numIn,
      out_meter:           numOut,
      prize_stock_count:   numStk,
      // ★D-608: 触っていない補充は列を作らない(null で入る)。0 を書かない。
      //   ★加算にはしない(却下3)。再送・オフライン同期で二重計上するため。
      prize_restock_count: numRst,
      play_price:          playPrice,
      entry_type:          entryType,
      source:              'manual',
      input_method:        'manual',
      created_by:          staffId ?? null,
      organization_id:     CHANGE_ORG_ID,
    }

    // 'replace' / 'collection' → 新規 INSERT
    if (entryType === 'replace' || entryType === 'collection') {
      const insertPayload = normalizeSlotStock({
        ...basePayload,
        ...mergedOptionalForInsert,
        reading_id: readingId ?? crypto.randomUUID(),
      })
      // ★D-608 ITEM_2: 補充がどう書かれたかを残す。新規行なので before は無い(null)。
      logger.info('patrol_restock_write', {
        booth_code: boothCode,
        patrol_date: today,
        staff_id: staffId ?? null,
        entry_type: entryType,
        path: 'insert',
        restock_provided: restockProvided,
        restock_before: null,
        restock_after: insertPayload.prize_restock_count ?? null,
      })
      const { data, error } = await supabase
        .from('meter_readings')
        .insert(insertPayload)
        .select('reading_id')
        .single()
      if (error) {
        // D2: 23505 = 同一 reading_id/一意制約 で既に配信済み → 冪等成功として扱う
        if (error.code === '23505') {
          logger.info('patrol_save_duplicate_skipped', { boothCode, patrol_date: today, entryType, readingId })
          // D-808 AC1: 既に配信済みで今回の INSERT は入っていない → 確定値は取れない。null(rekey は従来どおり)。
          return { ok: true, skipped: 'duplicate', entryType, readingId: readingId ?? null, savedOptional: null }
        }
        const ctx = { boothCode, patrol_date: today, entryType, raw: error }
        logger.error(ERR.METER_002, ctx)
        return { ok: false, errCode: ERR.METER_002, message: `${entryType} 記録エラー: ${error.message}`, raw: error }
      }
      logger.info('patrol_save_succeeded', { boothCode, entryType, readingId: data.reading_id })
      // D-808 AC1: サーバに実際に入った optional(prev の補完 + 触った列)。新しく計算せず INSERT に使った値を返す。
      return { ok: true, inserted: true, entryType, readingId: data.reading_id, savedOptional: mergedOptionalForInsert }
    }

    // 'patrol' → (booth_code, patrol_date) UPSERT
    const { data: existing } = await supabase
      .from('meter_readings')
      .select(TODAY_EXISTING_SELECT)
      .eq('booth_code', boothCode)
      .eq('patrol_date', today)
      .eq('entry_type', 'patrol')
      .maybeSingle()

    const patchKeys = Object.keys(patch)
    const optionalUnchanged =
      patchKeys.length === 0 ||
      patchKeys.every((k) => {
        const a = existing?.[k]
        const b = patch[k]
        // D-994: 報告は配列。中身で比べる(文字列化で [object Object] 同士が一致扱いになるのを防ぐ)。
        if (k === 'meter_exchanges') return sameExchanges(a, b)
        if (a == null && b == null) return true
        if (typeof a === 'number' || typeof b === 'number') {
          return Number(a) === Number(b)
        }
        return strNorm(a) === strNorm(b)
      })

    if (existing) {
      // D3: stale-guard — server 行が client record 作成時刻以降に更新済みなら書かない
      // (古い queued record が新しい手動入力を上書きしない。server-newer wins)。
      if (
        clientTimestamp &&
        existing.updated_at &&
        new Date(existing.updated_at).getTime() >= new Date(clientTimestamp).getTime()
      ) {
        // D-808 AC1: 書いていない(サーバ側が新しい)→ 今回の値は確定値ではない。null。
        return { ok: true, skipped: 'stale', entryType: 'patrol', readingId: existing.reading_id, savedOptional: null }
      }

      const coreUnchanged =
        Number(existing.in_meter)            === numIn  &&
        Number(existing.out_meter)           === numOut &&
        Number(existing.prize_stock_count)   === numStk &&
        // D-350 DEFECT_B: 補充未入力時は prize_restock_count を比較から外す(外すと変更なし判定が崩れ不要な UPDATE が走る)。
        (!restockProvided || Number(existing.prize_restock_count) === numRst)

      // D-234 IMPL_1: 変更なし早期 return でも entryType/readingId を返す(storeSync が rekey に使う)。
      //   判定・分岐は不変、戻り値への追加のみ。
      // D-808 AC1: 変更なし = サーバ行がそのまま確定値。★TODAY_EXISTING_SELECT に無い列(行2/3・prize_cost_1〜3)は拾えない(キー無しのまま)。
      if (coreUnchanged && optionalUnchanged) return { ok: true, skipped: true, entryType: 'patrol', readingId: existing.reading_id, savedOptional: pickOptionalKeys(existing) }

      const updatePayload = {
        in_meter:            numIn,
        out_meter:           numOut,
        prize_stock_count:   numStk,
        // D-350 DEFECT_B: 補充未入力時は prize_restock_count を送らない(既存値を保持)。明示 0/入力ありのみ送る。
        ...(restockProvided ? { prize_restock_count: numRst } : {}),
        entry_type:          'patrol',
        updated_at:          now,
        created_by:          staffId ?? null,
        ...patch,
      }
      // SPEC-LF1-REPLAY-CONSTRAINT-NORMALIZE-02: UPDATE の CHECK は結果行全体で評価される。
      // -01 の無条件 normalizeSlotStock は既存 stock_N を 0 で破壊した。既存行も stock_N が
      // 無い時だけ 0 を注入する (patch が out_meter_N を立て、patch/existing 両方 null)。
      if (patch.out_meter_2 != null && patch.stock_2 == null && existing.stock_2 == null) updatePayload.stock_2 = 0
      if (patch.out_meter_3 != null && patch.stock_3 == null && existing.stock_3 == null) updatePayload.stock_3 = 0

      // ★D-608 ITEM_2: 既存行の補充が何から何になったかを残す。
      //   ★restock_provided=false のときは列を送っていない=before がそのまま残る。
      //     「消えた」の再発を DB を掘らずに追えるようにするのが目的。
      logger.info('patrol_restock_write', {
        booth_code: boothCode,
        patrol_date: today,
        staff_id: staffId ?? null,
        entry_type: 'patrol',
        path: 'update',
        reading_id: existing.reading_id,
        restock_provided: restockProvided,
        restock_before: existing.prize_restock_count ?? null,
        restock_after: restockProvided ? numRst : (existing.prize_restock_count ?? null),
      })
      const { error } = await supabase
        .from('meter_readings')
        .update(updatePayload)
        .eq('reading_id', existing.reading_id)
      if (error) {
        const ctx = { boothCode, patrol_date: today, entryType, readingId: existing.reading_id, raw: error }
        logger.error(ERR.METER_001, ctx)
        return { ok: false, errCode: ERR.METER_001, message: 'メーター更新エラー: ' + error.message, raw: error }
      }
      logger.info('patrol_save_succeeded', { boothCode, entryType: 'patrol', readingId: existing.reading_id })
      // D-808 AC1: UPDATE は patch の列だけを書く → 確定値 = 既存行の値 + patch。
      return { ok: true, updated: true, entryType: 'patrol', readingId: existing.reading_id, savedOptional: { ...pickOptionalKeys(existing), ...patch } }
    }

    const patrolInsertPayload = normalizeSlotStock({
      ...basePayload,
      ...mergedOptionalForInsert,
      reading_id: readingId ?? crypto.randomUUID(),
    })
    const { data, error } = await supabase
      .from('meter_readings')
      .insert(patrolInsertPayload)
      .select('reading_id')
      .single()
    if (error) {
      // D2: 23505 = 既に配信済み (並行 replay / DB uq_meter_readings_patrol_day) → 冪等成功
      if (error.code === '23505') {
        logger.info('patrol_save_duplicate_skipped', { boothCode, patrol_date: today, entryType: 'patrol', readingId })
        return { ok: true, skipped: 'duplicate', entryType: 'patrol', readingId: readingId ?? null, savedOptional: null }
      }
      const ctx = { boothCode, patrol_date: today, entryType, raw: error }
      logger.error(ERR.METER_001, ctx)
      return { ok: false, errCode: ERR.METER_001, message: 'メーター保存エラー: ' + error.message, raw: error }
    }
    logger.info('patrol_save_succeeded', { boothCode, entryType: 'patrol', readingId: data.reading_id })
    return { ok: true, inserted: true, entryType: 'patrol', readingId: data.reading_id, savedOptional: mergedOptionalForInsert }

  } catch (e) {
    logger.error('patrol_save_failed_unexpected', { message: e?.message, boothCode, entryType })
    return { ok: false, errCode: ERR.METER_001, message: e?.message ?? '予期しないエラー', raw: e }
  }
}

/**
 * Stage 2 後方互換 wrapper
 * @deprecated Stage 3 では savePatrolReading を使うこと
 */
export async function upsertPatrolReading(args) {
  return savePatrolReading({ ...args, entryType: 'patrol' })
}

/**
 * SPEC-METER-DELTA-ANOMALY-CHECK-01 (D-357): メーター差分異常の事後検知トリガ(fire-and-forget)。
 * 検知本体は server 側 RPC fn_flag_meter_delta_anomaly(booth 単位キャリブレーション + マイナスdelta判定)。
 * ★保存フロー末尾から呼ぶが、保存を絶対にブロック・遅延・失敗させない=RPC の成否に関わらず throw/reject しない。
 *   検知はあくまで事後の要確認シグナルで、meter_readings の値は書き換えない(補正しない)。
 */
export async function flagMeterDeltaAnomaly(boothCode, readingId) {
  if (!boothCode || !readingId) return
  try {
    const { error } = await supabase.rpc('fn_flag_meter_delta_anomaly', {
      p_booth_code: boothCode,
      p_reading_id: readingId,
    })
    if (error) logger.warn?.('meter_delta_anomaly_flag_failed', { boothCode, readingId, message: error.message })
  } catch (e) {
    // 検知失敗は現場運用を止めない。黙って握り潰す(fire-and-forget の思想)。
    logger.warn?.('meter_delta_anomaly_flag_threw', { boothCode, readingId, message: e?.message })
  }
}

/**
 * SPEC-PRIZE-OUT-ANOMALY-ALERT-V1 (D-410): 払出(OUT)メーター異常の事後検知トリガ(fire-and-forget)。
 * 検知本体は server 側 RPC fn_flag_prize_out_anomaly(meter_layout の払出スロット毎に A/B/C ルール判定)。
 * ★flagMeterDeltaAnomaly と同じく保存を絶対にブロック・遅延・失敗させない。meter_readings は書き換えない。
 */
export async function flagPrizeOutAnomaly(boothCode, readingId) {
  if (!boothCode || !readingId) return
  try {
    const { error } = await supabase.rpc('fn_flag_prize_out_anomaly', {
      p_booth_code: boothCode,
      p_reading_id: readingId,
    })
    if (error) {
      logger.warn?.('prize_out_anomaly_flag_failed', { boothCode, readingId, message: error.message })
      reportPrizeOutAnomalyFailure(boothCode, error) // D-473 IMPL_6
    }
  } catch (e) {
    logger.warn?.('prize_out_anomaly_flag_threw', { boothCode, readingId, message: e?.message })
    reportPrizeOutAnomalyFailure(boothCode, e) // D-473 IMPL_6
  }
}

// SPEC-PATROL-BASELINE-FETCH-RESILIENCE-V1 (D-473) IMPL_6:
//   D-472 のスキーマキャッシュ凍結で fn_flag_prize_out_anomaly も 74 回すべて 404 だった
//   (並んで呼ばれる fn_flag_meter_delta_anomaly は 74 回すべて 204 = 影響はこの1本だけ)。
//   保存自体は成功しているので現場は止まらないが、景品払出の異常検知が丸一日動いていなかったことに
//   誰も気づかなかった。よって失敗を握り潰さず Sentry へ送る。
//   ★保存経路の成否には一切影響させない(throw しない / 戻り値を持たない = fire-and-forget のまま)。
//   行データと個人情報は送らない(boothHistory.js の reportBaselineIncomplete と同じ書き方)。
function reportPrizeOutAnomalyFailure(boothCode, err) {
  try {
    Sentry.captureMessage('prize-out-anomaly-flag-failed', {
      level: 'warning',
      tags: { diag: 'D-473', kind: 'prize-out-anomaly-failed' },
      extra: {
        store_code:    String(boothCode || '').split('-')[0] || null,
        error_code:    err?.code ?? null,
        error_message: err?.message ?? null,
      },
    })
  } catch { /* 計測失敗は保存経路を壊さない */ }
}
